/**
 * Bundled UI catalogs: `src/i18n/locales/<locale>/<ns>.json`.
 *
 * Webpack discovers every folder at build time and emits one lazy chunk per
 * file, so adding a language means adding a folder: nothing here enumerates
 * locales or namespaces. Jest has no `require.context`; `setupTests.js` swaps
 * this module for `catalogSource.jest.js`, which reads the same files from disk.
 */

type CatalogModule = { default?: Record<string, unknown> } & Record<string, unknown>;

interface LazyContext {
  keys(): string[];
  (key: string): Promise<CatalogModule>;
}

// The `as` cast is stripped by Babel, leaving the literal `require.context`
// call that webpack resolves statically.
const context = (require as unknown as {
  context: (dir: string, deep: boolean, filter: RegExp, mode: 'lazy') => LazyContext;
}).context('./locales', true, /^\.\/[^/]+\/[^/]+\.json$/, 'lazy');

const entries = context.keys()
  .map((key) => {
    const match = /^\.\/([^/]+)\/([^/]+)\.json$/.exec(key);
    return match ? { key, locale: match[1], ns: match[2] } : null;
  })
  .filter((entry): entry is { key: string; locale: string; ns: string } => entry !== null);

/** Locale folders present in the bundle, e.g. `['en', 'es', 'ru']`. */
export const bundledLocales: string[] = Array.from(new Set(entries.map((e) => e.locale))).sort();

/** Namespaces of the source language (`en`), which defines the full key set. */
export const bundledNamespaces: string[] = Array.from(
  new Set(entries.filter((e) => e.locale === 'en').map((e) => e.ns)),
).sort();

export const hasBundledCatalog = (locale: string, ns: string): boolean =>
  entries.some((e) => e.locale === locale && e.ns === ns);

/** Resolves to the catalog, or `{}` when this locale has no such file. */
export const loadBundledCatalog = (locale: string, ns: string): Promise<Record<string, unknown>> => {
  const entry = entries.find((e) => e.locale === locale && e.ns === ns);
  if (!entry) return Promise.resolve({});
  return context(entry.key).then((mod) => (mod.default ?? mod) as Record<string, unknown>);
};

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

/**
 * Shared namespace (`taxa`, `catalog`, `sim`, `errors`, `scenarios`) from
 * `GET /api/i18n/:locale/:ns`, already merged with the fallback chain by the
 * server. The browser HTTP cache revalidates it by ETag. Failure → `{}`, so
 * the UI falls back to keys/server text instead of breaking.
 */
export const loadSharedCatalog = (locale: string, ns: string): Promise<Record<string, unknown>> =>
  fetch(`${API_BASE}/i18n/${encodeURIComponent(locale)}/${encodeURIComponent(ns)}`)
    .then((res) => (res.ok ? res.json() : {}))
    .catch(() => ({}));

/** `GET /api/i18n/locales` → `{ source, locales: [{ code, name, nativeName }], namespaces }`. */
export const loadLocaleManifest = (): Promise<{ locales?: Array<{ code?: string; name?: string; nativeName?: string }> } | null> =>
  fetch(`${API_BASE}/i18n/locales`).then((res) => (res.ok ? res.json() : null));
